// Service worker registration and the update flow (Phase 11 PWA; the worker itself is src/pwa/sw.template.js,
// built by apps/web/pwa.ts). Registered only in production web builds on a secure origin — never in the dev server
// (Vite serves no sw.js and HMR must not be cached) and never in the desktop app (app:// renderer with its own
// updater: `window.moodsDesktop` present). Navigations always come from the network (src/pwa/sw.template.js), so a
// page that has just loaded already runs the newest deploy: an update found at load time (a worker left waiting by an
// earlier visit, or the one the browser installs while checking sw.js on this navigation) takes over quietly. Only an
// update found while the page has been open for a while means the running code is old: that one waits for the
// "Yenile" toast, which activates it and reloads once it controls the page.
// Web push (v1.1): a notification click in the worker focuses this window and posts NAVIGATE, re-dispatched as the
// `moods:navigate` window event (the shell routes it).
import { create } from 'zustand';

/** localStorage: the user id whose push subscription this browser holds (the app re-registers it on start). */
export const PUSH_OWNER_KEY = 'moods.push.user';

/** Signing out: this browser stops being a push device (subscription dropped, no re-registration for the next user). */
export function forgetPushSubscription(win: Window = window): void {
  try {
    win.localStorage.removeItem(PUSH_OWNER_KEY);
  } catch {
    // storage blocked: nothing was stored either
  }
  if (typeof win.navigator === 'undefined' || !('serviceWorker' in win.navigator)) return;
  void win.navigator.serviceWorker
    .getRegistration()
    .then(reg => reg?.pushManager?.getSubscription())
    .then(sub => sub?.unsubscribe())
    .catch(() => undefined);
}

export interface SwEnvironment {
  production: boolean;
  /** the Electron bridge (`window.moodsDesktop`) exists */
  desktop: boolean;
  supported: boolean;
  secureContext: boolean;
  /** `localStorage['moods.sw'] === 'off'` (support / debugging escape hatch) */
  optedOut?: boolean;
}

/** Pure gate (unit-tested). */
export function shouldRegisterServiceWorker(env: SwEnvironment): boolean {
  return env.production && !env.desktop && env.supported && env.secureContext && !env.optedOut;
}

export function currentSwEnvironment(win: Window = window): SwEnvironment {
  let optedOut: boolean;
  try {
    optedOut = win.localStorage.getItem('moods.sw') === 'off';
  } catch {
    optedOut = false;
  }
  return {
    production: import.meta.env.PROD,
    desktop: typeof (win as Window & { moodsDesktop?: unknown }).moodsDesktop !== 'undefined',
    supported: typeof win.navigator !== 'undefined' && 'serviceWorker' in win.navigator,
    secureContext: Boolean(win.isSecureContext),
    optedOut,
  };
}

interface SwUpdateState {
  /** a new worker installed and waiting for the page's go-ahead */
  waiting: ServiceWorker | null;
  setWaiting: (w: ServiceWorker | null) => void;
}

export const useSwUpdate = create<SwUpdateState>(set => ({ waiting: null, setWaiting: waiting => set({ waiting }) }));

/** "Yenile": activate the waiting worker, reload once it controls the page. */
export function applySwUpdate(win: Window = window): void {
  const waiting = useSwUpdate.getState().waiting;
  if (!waiting) {
    win.location.reload();
    return;
  }
  let reloaded = false;
  win.navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    win.location.reload();
  });
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

const UPDATE_CHECK_MS = 60 * 60 * 1000;
/** an update installed within this time after the page loaded belongs to the deploy the page already runs */
export const FRESH_LOAD_MS = 60_000;

/** Pure (unit-tested): take a freshly installed update over without asking, or offer it through the toast. */
export function updateDecision(msSinceLoad: number): 'activate' | 'offer' {
  return msSinceLoad < FRESH_LOAD_MS ? 'activate' : 'offer';
}

/** Registers /sw.js when the gate allows it; returns whether it tried. */
export function registerServiceWorker(win: Window = window, env: SwEnvironment = currentSwEnvironment(win)): boolean {
  if (!shouldRegisterServiceWorker(env)) return false;
  const start = () => {
    const sw = win.navigator.serviceWorker;
    sw.addEventListener('message', (e: MessageEvent) => {
      const d = e.data as { type?: unknown; url?: unknown } | null;
      if (d?.type === 'NAVIGATE' && typeof d.url === 'string')
        win.dispatchEvent(new CustomEvent('moods:navigate', { detail: d.url }));
    });
    sw.startMessages?.();
    void sw
      .register('/sw.js', { scope: '/' })
      .then(reg => {
        const take = (worker: ServiceWorker) => {
          if (updateDecision(win.performance.now()) === 'activate') worker.postMessage({ type: 'SKIP_WAITING' });
          else useSwUpdate.getState().setWaiting(worker);
        };
        const track = (worker: ServiceWorker | null) => {
          if (!worker) return;
          const check = () => {
            // "installed" while another worker controls the page = an update (the first install has no controller)
            if (worker.state === 'installed' && sw.controller) take(worker);
          };
          worker.addEventListener('statechange', check);
          check();
        };
        // a worker still waiting from an earlier visit: this page is newer than the one it was offered to
        if (reg.waiting && sw.controller) take(reg.waiting);
        reg.addEventListener('updatefound', () => track(reg.installing));
        win.setInterval(() => void reg.update().catch(() => undefined), UPDATE_CHECK_MS);
      })
      .catch((err: unknown) => console.warn('[sw] registration failed', err));
  };
  if (win.document.readyState === 'complete') start();
  else win.addEventListener('load', start, { once: true });
  return true;
}
